import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { Body } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Flex } from '@cognite/cogs-core-v11/components/flex/flex.tsx';
import { Input } from '@cognite/cogs-core-v11/components/input/input.tsx';

import { ComponentContainer } from '../../ui/component/component-container/component-container';
import { useUpsertAppConfigMutation } from '../hooks/use-mutation/use-upsert-app-config-mutation/use-upsert-app-config-mutation';
import { useAppConfigQuery } from '../hooks/use-query/use-app-config-query';
import { SaveConfigurationButton } from '../save-configuration/save-configuration-button';
import type { AppConfig } from '../types';

import * as S from './elements';

interface Props {
  hasAppConfigPermission: boolean;
}

export const AppConfigDocument: FC<Props> = ({ hasAppConfigPermission }) => {
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [documentType, setDocumentType] = useState('');

  const { data: appConfig, isSuccess, isLoading } = useAppConfigQuery();
  const { mutateAsync: upsertAppConfig } = useUpsertAppConfigMutation();
  const documentConfiguration = appConfig?.featureConfiguration?.documents;

  useEffect(() => {
    if (isSuccess && documentConfiguration) {
      if (documentConfiguration.title) {
        setDocumentTitle(documentConfiguration.title);
      }
      if (documentConfiguration.description) {
        setDocumentDescription(documentConfiguration.description);
      }
      if (documentConfiguration.type) {
        setDocumentType(documentConfiguration.type);
      }
    }
  }, [isSuccess, documentConfiguration, appConfig]);

  const hasConfigChanged =
    documentTitle !== (documentConfiguration?.title ?? '') ||
    documentDescription !== (documentConfiguration?.description ?? '') ||
    documentType !== (documentConfiguration?.type ?? '');

  const handleSave = async () => {
    if (!appConfig) return;

    const newAppConfig: AppConfig = {
      ...appConfig,
      featureConfiguration: {
        ...appConfig.featureConfiguration,
        documents: {
          ...appConfig.featureConfiguration?.documents,
          title: documentTitle,
          description: documentDescription,
          type: documentType,
        },
      },
    };

    await upsertAppConfig({
      newAppConfig,
    });
  };

  const documentCardConfigs = (
    <Flex direction="column" gap={8}>
      <Body size="x-small">
        Configure how the documents shall display data in the asset document
        card or document full page. Data is fetched from CDF files. Fields are
        configured by its CDF field name, eg *id*, *name* or *metadata.title*.
      </Body>
      <Flex gap={16}>
        <Flex direction="column" gap={8}>
          <Body size="x-small" strong>
            Title
          </Body>
          <Input
            disabled={!hasAppConfigPermission}
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
          />
          <Body size="x-small">
            This is the document title displayed for each document
          </Body>
        </Flex>
      </Flex>
      <Flex gap={16}>
        <Flex direction="column" gap={8}>
          <Body size="x-small" strong>
            Description
          </Body>
          <Input
            disabled={!hasAppConfigPermission}
            value={documentDescription}
            onChange={(e) => setDocumentDescription(e.target.value)}
          />
          <Body size="x-small">
            This is the document description displayed for each document under
            the title.
          </Body>
        </Flex>
      </Flex>
      <Flex gap={16}>
        <Flex direction="column" gap={8}>
          <Body size="x-small" strong>
            Type
          </Body>
          <Input
            disabled={!hasAppConfigPermission}
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
          />
          <Body size="x-small">
            When document types is configured, they are grouped in the document
            page.
          </Body>
        </Flex>
      </Flex>
    </Flex>
  );

  return (
    <ComponentContainer>
      <S.Wrapper>
        <Flex direction="column" gap={16}>
          {documentCardConfigs}
          <SaveConfigurationButton
            onSave={handleSave}
            isDisabled={!hasConfigChanged}
            isLoading={isLoading}
          />
        </Flex>
      </S.Wrapper>
    </ComponentContainer>
  );
};
